import React from 'react';
import { motion } from 'framer-motion';
import { PhoneForwarded, ChevronRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
export type VexuStatus = 'active' | 'busy' | 'dnd';
interface VexuNotificationProps {
  status: VexuStatus;
  onTap?: () => void;
}
const STATUS_CONFIG: Record<VexuStatus, {
  label: string;
  sub: string;
  dot: string;
}> = {
  active: {
    label: 'Active',
    sub: 'Forwarding & answering your calls',
    dot: 'bg-vexu'
  },
  busy: {
    label: 'Busy',
    sub: "Handling calls while you're unavailable",
    dot: 'bg-amber-500'
  },
  dnd: {
    label: 'Do Not Disturb',
    sub: 'Screening every incoming call',
    dot: 'bg-purple-500'
  }
};
export function VexuNotification({
  status,
  onTap
}: VexuNotificationProps) {
  const {
    isLight
  } = useTheme();
  const cfg = STATUS_CONFIG[status];
  return <motion.button type="button" onClick={onTap} initial={{
    opacity: 0,
    y: -24,
    scale: 0.96
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: -24,
    scale: 0.96
  }} transition={{
    type: 'spring',
    stiffness: 380,
    damping: 30
  }} className={`absolute top-14 left-3 right-3 z-[45] w-auto text-left rounded-[20px] p-3 shadow-xl active:scale-[0.985] transition-transform ${isLight ? 'bg-white/95 backdrop-blur-2xl ring-1 ring-black/5 shadow-black/10' : 'bg-[#1c1c1e]/95 backdrop-blur-2xl ring-1 ring-white/10 shadow-black/40'}`} data-id="element-1233" data-mp="3tzsr04limmhz">
      <div className="flex items-center gap-3" data-id="element-1234" data-mp="1elcdihqry10k">
        {/* App icon */}
        <div className="relative flex-shrink-0" data-id="element-1235" data-mp="1engdwppgshw3">
          <div className="w-9 h-9 rounded-[10px] bg-vexu flex items-center justify-center shadow-sm shadow-vexu/30" data-id="element-1236" data-mp="1jj5gl1ws6ewm">
            <PhoneForwarded className="w-5 h-5 text-white" strokeWidth={2.2} data-id="element-1237" data-mp="1nnd8f7yjcaz2" />
          </div>
          {/* ongoing pulse */}
          <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5" data-id="element-1238" data-mp="1sislvysms4uy">
            <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${cfg.dot}`} data-id="element-1239" data-mp="3oojfeyjv48ft" />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ring-2 ${cfg.dot} ${isLight ? 'ring-white' : 'ring-[#1c1c1e]'}`} data-id="element-1240" data-mp="28fb5pjipyht" />
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0" data-id="element-1241" data-mp="c63ciybmq48y">
          <div className="flex items-center justify-between gap-2" data-id="element-1242" data-mp="c4s0ycbxzs7a">
            <div className="flex items-center gap-1.5 min-w-0" data-id="element-1243" data-mp="76gr41lndhp2">
              <span className={`text-[13px] font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1244" data-mp="l7b4kynjy8ul">
                Vexu
              </span>
              <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${status === 'active' ? isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu' : status === 'busy' ? isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-400' : isLight ? 'bg-purple-50 text-purple-700' : 'bg-purple-500/15 text-purple-400'}`} data-id="element-1245" data-mp="g6dl8iqt38ri">
                {cfg.label}
              </span>
            </div>
            <span className={`text-[10px] font-medium flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1246" data-mp="oo5uq6oh95ru">
              now
            </span>
          </div>
          <p className={`text-[12px] leading-snug mt-0.5 truncate ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1247" data-mp="epzqhif77njt">
            {cfg.sub}
          </p>
        </div>

        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1248" data-mp="5r4nsgbd4vl9" />
      </div>

      {/* Ongoing footer */}
      <div className={`mt-2 pt-2 border-t flex items-center gap-1.5 ${isLight ? 'border-slate-100' : 'border-white/[0.06]'}`} data-id="element-1249" data-mp="204dxa83wsh3i">
        <span className={`text-[10px] font-medium uppercase tracking-wider ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1250" data-mp="244xs3510f8mh">
          Ongoing · Call forwarding on
        </span>
      </div>
    </motion.button>;
}